import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../constants/firebaseModular';
import { PatientDoc } from '../types/Patient/PatientDoc';
import * as Sentry from '@sentry/browser';

function useRealTimeQuery<Data>(patientId: string) {
  const queryClient = useQueryClient();

  // THIS EFFECT OPENS A WEB-SOCKET TO THE DATABASE AND UPDATES THE QUERY CACHE WITH THE LATEST DATA
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'users', `${patientId}`),
      (doc) => {
        if (!doc.exists()) {
          // TODO: CONSIDER REMOVING THIS ERROR AS IT WILL HAPPEN FOR EVERY NEW PATIENT
          Sentry.captureMessage(`Document does not exist.`);
          console.error(`Document does not exist.`);
          return;
        }
        const data = doc.data() as PatientDoc;
        queryClient.setQueryData(['patientDoc', patientId], data);
        return data;
      },
      (error) => {
        if (patientId !== '123') {
          Sentry.captureMessage(
            `A(n) ${error.name} error occurred fetching patient info. Please copy/paste the information below when contacting support:\nCODE: ${error.code},\nMESSAGE: ${error.message}\nCUSTOM_DATA: ${error.customData}`,
          );
          console.error('Error:', error);
          console.error('Error string:', JSON.stringify(error));
          // console.error(
          //   `A(n) ${error.name} error occurred. Please copy/paste the information below when contacting support:\nCODE: ${error.code},\nMESSAGE: ${error.message}\nCUSTOM_DATA: ${error.customData}`,
          // );
          alert(`Error loading patient document data.\nPlease contact support if this issue persists.`);
          queryClient.invalidateQueries({ queryKey: ['patientDoc', patientId] });
          return error;
        }
      },
    );
    return () => unsubscribe();
  }, [queryClient, patientId]);

  // Here we return a query that exists to establish the index where we write the data into the QueryCache.
  // But because the useEffect establishes a listener, this query will always be in the 'isFetching' (not 'isLoading') state according to the queryClient.
  return useQuery<Data, Error>({
    queryKey: ['patientDoc', patientId],
    // The queryFn will instantly resolve with no value.
    // Therefore, the 'isLoading' property will almost instantly be false and the 'error' property will ALWAYS be undefined.
    queryFn: () => new Promise<Data>(() => {}),
  });
}

export default useRealTimeQuery;
